import { createHttpClient } from '../../../api/http';
import { serviceUrls } from '../../../config/serviceUrls';
import type { Project } from '@ibook/types';
import type { PageTextExtraction } from '@ibook/types/extraction';

export type IbookStatus = 'draft' | 'processing' | 'ready' | 'published' | 'archived' | 'failed';

/** Server-side DevTeam master workspace marker (not sent as x-tenant-id). */
export const DEVTEAM_WORKSPACE_SCOPE = 'devteam';

/** Legacy internal storage tenant for DevTeam workspace drafts. */
export const LEGACY_DEVTEAM_WORKSPACE_TENANT = '__devteam__';

export function isDevteamWorkspaceBook(record: {
  tenant_id?: string;
  workspace_scope?: string;
}): boolean {
  return (
    record.workspace_scope === DEVTEAM_WORKSPACE_SCOPE ||
    record.tenant_id === LEGACY_DEVTEAM_WORKSPACE_TENANT
  );
}

export interface StudentIbookRecord {
  book_id: string;
  title: string;
  description: string;
  language: string;
  grade: string;
  curriculum_tags: string[];
  page_count: number;
  cover_url?: string | null;
  reader_type?: 'ibook' | 'interactive_book';
  publication_id?: string | null;
}

export interface IbookRecord {
  book_id: string;
  tenant_id: string;
  workspace_scope?: string;
  title: string;
  description: string;
  language: string;
  grade: string;
  curriculum_tags: string[];
  status: IbookStatus;
  schema_version: string;
  revision: number;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  published_by: string | null;
  page_count: number;
  cover_url?: string | null;
  author_name?: string;
  created_by_display_name?: string;
}

export interface IbookDetail extends IbookRecord {
  project: Project | null;
}

export interface IbookListFilters {
  status?: string;
  language?: string;
  grade?: string;
  title?: string;
  skip?: number;
  limit?: number;
}

export interface PublishIbookPayload {
  tenant_id: string;
  title?: string;
  description?: string;
  language?: string;
  grade?: string;
  curriculum_tags?: string[];
  publish_notes?: string;
}

export interface IbookListResponse {
  items: IbookRecord[];
  total: number;
  limit: number;
  offset: number;
}

export interface IbookPublicationRecord {
  publication_id: string;
  book_id: string;
  target_tenant_id: string;
  title: string;
  description: string;
  language: string;
  grade: string;
  status: string;
  published_revision: number;
  page_count: number;
  published_at: string | null;
  reader_type?: 'ibook' | 'interactive_book';
  has_cover?: boolean;
}

export interface IbookPublicationListResponse {
  items: IbookPublicationRecord[];
  total: number;
}

const client = createHttpClient(serviceUrls.gateway, { timeoutMs: 120_000 });

function normalizeListPayload(data: IbookListResponse | IbookRecord[]): IbookRecord[] {
  if (Array.isArray(data)) return data;
  return data.items ?? [];
}

export async function listIbooks(filters: IbookListFilters = {}): Promise<IbookRecord[]> {
  const res = await client.get<IbookListResponse | IbookRecord[]>('/v1/ibooks', {
    params: filters,
  });
  return normalizeListPayload(res.data);
}

export async function listIbooksPage(filters: IbookListFilters = {}): Promise<IbookListResponse> {
  const res = await client.get<IbookListResponse | IbookRecord[]>('/v1/ibooks', {
    params: filters,
  });
  const data = res.data;
  if (Array.isArray(data)) {
    return { items: data, total: data.length, limit: filters.limit ?? 50, offset: filters.skip ?? 0 };
  }
  return data;
}

export async function getIbook(bookId: string): Promise<IbookDetail> {
  const res = await client.get<IbookDetail>(`/v1/ibooks/${bookId}`);
  return res.data;
}

export async function createIbook(payload: {
  title: string;
  description?: string;
  language?: string;
  grade?: string;
  curriculum_tags?: string[];
  author_name?: string;
}): Promise<IbookRecord> {
  const res = await client.post<IbookRecord>('/v1/ibooks', payload);
  return res.data;
}

export async function updateIbook(
  bookId: string,
  payload: {
    title?: string;
    description?: string;
    language?: string;
    grade?: string;
    curriculum_tags?: string[];
    project?: Project;
    revision?: number;
  },
): Promise<IbookDetail> {
  const res = await client.patch<IbookDetail>(`/v1/ibooks/${bookId}`, payload);
  return res.data;
}

export async function deleteIbook(bookId: string): Promise<void> {
  const res = await client.delete(`/v1/ibooks/${bookId}`, {
    validateStatus: (status) => status === 200 || status === 204,
  });
  if (res.status === 204 || res.data == null || res.data === '') {
    return;
  }
}

export async function listTenantIbookPublications(
  targetTenantId: string,
): Promise<IbookPublicationRecord[]> {
  const res = await client.get<IbookPublicationListResponse>('/v1/ibooks/publications', {
    params: { target_tenant_id: targetTenantId },
  });
  return res.data.items ?? [];
}

export async function unpublishIbook(bookId: string, tenantId: string): Promise<void> {
  await client.post(`/v1/ibooks/${bookId}/unpublish`, { tenant_id: tenantId });
}

export async function retractIbookFromAllSchools(bookId: string): Promise<number> {
  const res = await client.post<{ publications_archived?: number }>(
    `/v1/ibooks/${bookId}/retract-publications`,
  );
  return Number(res.data?.publications_archived ?? 0);
}

export interface IbookAssetUploadResult {
  asset_id: string;
  storage_path: string;
  signed_url?: string | null;
  binary_stored: boolean;
  storage_backend: string;
  gcs_uploaded: boolean;
  size_bytes: number;
  sha256: string;
  content_type: string;
  skipped: boolean;
}

export interface IbookPublishJob {
  publication_job_id: string;
  status: 'processing' | 'published' | 'failed';
  book_id: string;
  target_tenant_id?: string | null;
  published_revision?: number | null;
  publication_id?: string | null;
  manifest_id?: string | null;
  error?: string | null;
  result?: IbookRecord | null;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export async function getPublishJobStatus(bookId: string, jobId: string): Promise<IbookPublishJob> {
  const res = await client.get<IbookPublishJob>(`/v1/ibooks/${bookId}/publish-status/${jobId}`);
  return res.data;
}

export async function publishIbook(bookId: string, payload: PublishIbookPayload): Promise<IbookRecord> {
  const res = await client.post<IbookRecord | IbookPublishJob>(
    `/v1/ibooks/${bookId}/publish`,
    payload,
    { validateStatus: (status) => status === 200 || status === 202 },
  );
  if (res.status === 200) {
    return res.data as IbookRecord;
  }
  const job = res.data as IbookPublishJob;
  const deadline = Date.now() + 120_000;
  while (Date.now() < deadline) {
    await sleep(1000);
    const status = await getPublishJobStatus(bookId, job.publication_job_id);
    if (status.status === 'published' && status.result) {
      return status.result;
    }
    if (status.status === 'failed') {
      throw new Error(status.error || 'Publish failed');
    }
  }
  throw new Error('Publish timed out — check publish status and retry if needed');
}

export async function listStudentPublishedIbooks(
  filters: Omit<IbookListFilters, 'status'> = {},
): Promise<StudentIbookRecord[]> {
  const res = await client.get<{ items: StudentIbookRecord[] } | StudentIbookRecord[]>('/v1/ibooks', {
    params: { ...filters, status: 'published' },
  });
  const data = res.data;
  if (Array.isArray(data)) return data;
  return data.items ?? [];
}

export async function getStudentIbookManifest(bookId: string) {
  return getIbookManifest(bookId);
}

export async function getIbookManifest(bookId: string, revision?: number) {
  const res = await client.get(`/v1/ibooks/${bookId}/manifest`, {
    params: revision != null ? { revision } : undefined,
  });
  return res.data as {
    book_id: string;
    tenant_id: string;
    revision: number;
    status: IbookStatus;
    manifest: { project: Project; extractions?: Record<string, PageTextExtraction> };
    signed_assets: Record<string, string>;
  };
}

export async function savePageExtraction(
  bookId: string,
  pageId: string,
  extraction: PageTextExtraction,
): Promise<void> {
  await client.post(`/v1/ibooks/${bookId}/pages/${pageId}/extraction`, extraction);
}

export async function uploadIbookAsset(
  bookId: string,
  assetId: string,
  blob: Blob,
  filename?: string,
): Promise<IbookAssetUploadResult> {
  const form = new FormData();
  form.append('asset_id', assetId);
  form.append('file', blob, filename || assetId);
  const res = await client.post(`/v1/ibooks/${bookId}/assets`, form);
  return res.data as IbookAssetUploadResult;
}
